import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import MetricLineChart from './MetricLineChart';

const METRICS = [
    { key: 'totalLeads', label: 'Leads', color: '#10b981' },
    { key: 'bookedAppointments', label: 'Booked', color: '#3b82f6' },
    { key: 'attendedConsultations', label: 'Attended', color: '#8b5cf6' },
    { key: 'startedTreatments', label: 'Started', color: '#f59e0b' },
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TOGGLE_SX = {
    '& .MuiToggleButton-root': {
        textTransform: 'none',
        px: 2,
        py: 0.5,
        fontWeight: 500,
        fontSize: '13px',
        border: '1px solid #e2e8f0',
        '&.Mui-selected': {
            bgcolor: '#3b82f6',
            color: '#fff',
            '&:hover': { bgcolor: '#2563eb' },
        },
    },
};

export default function PracticePerformanceChart({ data, monthlyByPractice }) {
    const [selectedMetric, setSelectedMetric] = useState('totalLeads');
    // 'comparison' = per-practice bars (existing); 'trend' = monthly Leads line per practice.
    const [viewMode, setViewMode] = useState('comparison');

    // Monthly Leads trend — one line per practice, aligned to the shared month axis.
    const { trendCategories, trendSeries } = useMemo(() => {
        const monthsSet = new Set();
        (monthlyByPractice || []).forEach((p) => (p.months || []).forEach((m) => monthsSet.add(m.month)));
        const monthsSorted = [...monthsSet].sort((a, b) => a - b);
        const byId = Object.fromEntries((monthlyByPractice || []).map((p) => [String(p.practiceId), p]));
        const series = (data || []).map((prac) => {
            const monthMap = Object.fromEntries((byId[String(prac.practiceId)]?.months || []).map((m) => [m.month, m.count]));
            return { name: prac.practiceName || `Practice ${prac.practiceId}`, data: monthsSorted.map((mo) => monthMap[mo] || 0) };
        });
        return { trendCategories: monthsSorted.map((mo) => MONTH_LABELS[mo - 1] || String(mo)), trendSeries: series };
    }, [data, monthlyByPractice]);

    if (!data?.length) return null;

    const metric = METRICS.find((m) => m.key === selectedMetric) || METRICS[0];
    const maxValue = Math.max(...data.map((d) => d[selectedMetric] || 0), 1);

    // Sort data by selected metric
    const sortedData = [...data].sort((a, b) => (b[selectedMetric] || 0) - (a[selectedMetric] || 0));

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                mb: 3,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                    Practice Performance
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {/* View: per-practice Comparison (bars) vs monthly Trend (line) */}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, value) => value && setViewMode(value)}
                        size="small"
                        sx={TOGGLE_SX}
                    >
                        <ToggleButton value="comparison">Comparison</ToggleButton>
                        <ToggleButton value="trend">Trend</ToggleButton>
                    </ToggleButtonGroup>

                    {/* Metric selector applies to the Comparison (bar) view */}
                    {viewMode === 'comparison' && (
                        <ToggleButtonGroup
                            value={selectedMetric}
                            exclusive
                            onChange={(e, value) => value && setSelectedMetric(value)}
                            size="small"
                            sx={TOGGLE_SX}
                        >
                            {METRICS.map((m) => (
                                <ToggleButton key={m.key} value={m.key}>
                                    {m.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}
                </Box>
            </Box>

            {/* Trend view — monthly Leads per practice */}
            {viewMode === 'trend' && (
                trendSeries.length > 0 && trendCategories.length > 0 ? (
                    <MetricLineChart
                        categories={trendCategories}
                        series={trendSeries}
                        type="line"
                        height={360}
                        yAxisTitle="Leads"
                        maxCategories={12}
                    />
                ) : (
                    <Typography sx={{ fontSize: '14px', color: '#64748b', py: 4, textAlign: 'center' }}>
                        No monthly lead data for the selected range.
                    </Typography>
                )
            )}

            {/* Comparison view — per-practice bars */}
            {viewMode === 'comparison' && (
            <>
            {/* Bar Chart */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sortedData.map((practice, index) => {
                    const value = practice[selectedMetric] || 0;
                    const percentage = (value / maxValue) * 100;

                    return (
                        <Box key={practice.practiceId}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 0.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            color: '#64748b',
                                            width: 20,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {index + 1}
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                        {practice.practiceName}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: metric.color,
                                    }}
                                >
                                    {value}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    height: 24,
                                    bgcolor: '#f1f5f9',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        width: `${percentage}%`,
                                        background: `linear-gradient(90deg, ${metric.color}, ${metric.color}dd)`,
                                        borderRadius: '6px',
                                        transition: 'width 0.5s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        px: 1,
                                    }}
                                >
                                    {percentage > 20 && (
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: '#fff',
                                            }}
                                        >
                                            {((value / sortedData.reduce((sum, p) => sum + (p[selectedMetric] || 0), 0)) * 100).toFixed(0)}%
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Legend */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 3,
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #e2e8f0',
                }}
            >
                {METRICS.map((m) => (
                    <Box
                        key={m.key}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            opacity: selectedMetric === m.key ? 1 : 0.5,
                            transition: 'opacity 0.2s',
                            '&:hover': { opacity: 1 },
                        }}
                        onClick={() => setSelectedMetric(m.key)}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '3px',
                                bgcolor: m.color,
                            }}
                        />
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                            {m.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
            </>
            )}
        </Box>
    );
}

PracticePerformanceChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            practiceName: PropTypes.string,
            totalLeads: PropTypes.number,
            bookedAppointments: PropTypes.number,
            attendedConsultations: PropTypes.number,
            startedTreatments: PropTypes.number,
        })
    ),
    // Per-practice monthly lead counts for the Trend (line) view.
    monthlyByPractice: PropTypes.arrayOf(
        PropTypes.shape({
            practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            months: PropTypes.arrayOf(PropTypes.shape({
                month: PropTypes.number,
                count: PropTypes.number,
            })),
        })
    ),
};

PracticePerformanceChart.defaultProps = {
    data: [],
    monthlyByPractice: [],
};
